import Phaser from "phaser";
import { registerSW } from "virtual:pwa-register";
import { BRAND, COPY } from "./config/brand";
import {
  CAMPAIGN_LEVELS,
  CAMPAIGN_TRANSITION_MS,
  getCampaignLevel,
  type CampaignLevel,
} from "./config/campaign";
import { GAME_HEIGHT, GAME_WIDTH } from "./config/game";
import type { WeaponKind } from "./config/game";
import { AudioDirector } from "./game/audio";
import { BootScene } from "./game/BootScene";
import { EVENT, type HudDetail, type ResultDetail } from "./game/events";
import { GameScene, type GameSession } from "./game/GameScene";
import {
  comfyModeAvailable,
  completionPercent,
  isLevelUnlocked,
  recordRun,
  selectWeapon,
  setNickname,
  type PlayerProgress,
  type RecentRun,
} from "./game/progression";
import { renderShareCard, shareText } from "./game/share";
import {
  PreferenceStore,
  ProgressStore,
  type Preferences,
} from "./game/storage";
import { TiltController } from "./game/tilt";

function required<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing required element: ${selector}`);
  return element;
}

const TOUR_PANEL_CONTROLS: ReadonlyArray<readonly [string, string]> = [
  ["#change-launcher-action", "#launcher-panel"],
  ["#view-levels-action", "#tour-route-panel"],
  ["#view-rewards-action", "#tour-rewards-panel"],
];

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>'"]/g,
    (character) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[
        character
      ] ?? character,
  );
}

export class ButtroidsApp {
  private readonly store = new PreferenceStore();
  private preferences = this.store.load();
  private readonly progressStore = new ProgressStore();
  private progress: PlayerProgress = this.progressStore.load(
    this.preferences.bestScore,
  );
  private readonly audio = new AudioDirector(this.preferences);
  private readonly tilt = new TiltController();
  private game!: Phaser.Game;
  private gameReady = false;
  private playing = false;
  private paused = false;
  private tutorialVisible = false;
  private settingsShouldResume = false;
  private pendingPwaUpdate = false;
  private applyPwaUpdate: (() => Promise<void>) | null = null;
  private bonusGuideTimer: ReturnType<typeof globalThis.setTimeout> | null =
    null;
  private bonusGuideState: string | null = null;
  private campaignTransitionTimer: ReturnType<
    typeof globalThis.setTimeout
  > | null = null;
  private activeSession: GameSession = {
    mode: "quick",
    level: null,
    selectedWeapon: "poop",
    comfyMode: false,
    bestScore: this.progress.quickPlayBest,
    continued: false,
  };
  private lastRun: RecentRun | null = null;

  mount(root: HTMLElement): void {
    root.innerHTML = this.markup();
    this.bindUi();
    this.syncSettings();
    this.syncPlayerUi();
    this.createGame();
    this.registerPwa();
    this.bindGameEvents();
    this.exposeLocalTestApi();
  }

  private markup(): string {
    const ui = COPY.ui;
    return `
      <main class="app-shell" aria-label="${escapeHtml(`${BRAND.workingTitle} ${ui.aria.game}`)}">
        <aside class="side-panel side-panel--left" aria-label="${escapeHtml(ui.aria.gameInformation)}">
          <div class="brand-lockup">
            <div class="brand-mark" aria-hidden="true"><img src="./art/butt-hero.png" alt="" /></div>
            <p class="eyebrow">${escapeHtml(ui.identity.workingTitle)}</p>
            <h1>${escapeHtml(BRAND.workingTitle)}</h1>
            <p class="tagline">${escapeHtml(BRAND.tagline)}</p>
          </div>
          <div class="mission-card">
            <img class="mission-card__icon" src="./art/toilet-regular-v2.png" alt="" aria-hidden="true" />
            <div><strong>${escapeHtml(ui.identity.mission)}</strong><span>${escapeHtml(ui.identity.missionText)}</span></div>
          </div>
          <div class="desktop-help">
            <h2>${escapeHtml(ui.identity.keyboard)}</h2>
            <p><kbd>←</kbd> <kbd>→</kbd> ${escapeHtml(ui.identity.keyboardOr)} <kbd>A</kbd> <kbd>D</kbd> ${escapeHtml(ui.identity.keyboardMove)}</p>
            <p>${escapeHtml(ui.identity.autoFire)}</p>
          </div>
        </aside>

        <section class="game-column" aria-label="${escapeHtml(ui.aria.gameArea)}">
          <div class="hud" id="hud" aria-label="${escapeHtml(ui.hud.gameStatus)}" hidden>
            <div class="hud__stat"><span>${escapeHtml(ui.common.score)}</span><strong id="hud-score">0</strong></div>
            <div class="hud__stat hud__stat--center"><span id="hud-wave">${escapeHtml(ui.hud.initialWave)}</span><strong id="hud-clogs">${escapeHtml(ui.hud.initialClogs)}</strong><small id="hud-power">${escapeHtml(ui.hud.autoFireReady)}</small></div>
            <div class="hud__stat hud__stat--right"><span>${escapeHtml(ui.common.lives)}</span><strong id="hud-lives" aria-label="3 ${escapeHtml(ui.aria.lives)}">🧻 🧻 🧻</strong></div>
          </div>

          <div class="game-frame" id="game-frame">
            <div id="game-container" role="application" aria-label="${escapeHtml(`${BRAND.workingTitle} ${ui.aria.actionPlayfield}`)}"></div>
            <div class="bonus-guide" id="bonus-guide" role="status" aria-label="${escapeHtml(ui.controls.bonusGuide)}" hidden></div>
            <section class="level-transition" id="level-transition" role="status" aria-live="assertive" aria-labelledby="transition-next" hidden>
              <p class="eyebrow" id="transition-cleared">Level cleared!</p>
              <h2 id="transition-next">Next · Level 2 of 10</h2>
              <p class="level-transition__name" id="transition-name">Sideways Swirl</p>
              <p class="level-transition__pace" id="transition-pace">Fleet speed · 1.22×</p>
              <p class="level-transition__stars" id="transition-stars" aria-label="No stars earned"></p>
              <p class="level-transition__reward" id="transition-reward" hidden></p>
            </section>

            <section class="screen screen--menu" id="menu-screen" aria-labelledby="menu-title">
              <div class="hero-orbit" aria-hidden="true">
                <img class="hero-orbit__butt" src="./art/butt-hero.png" alt="" />
                <img class="hero-orbit__toilet" src="./art/toilet-regular-v2.png" alt="" />
              </div>
              <p class="eyebrow">${escapeHtml(ui.menu.kicker)}</p>
              <h2 id="menu-title">${escapeHtml(ui.menu.title)}</h2>
              <p>${escapeHtml(ui.menu.description)}</p>
              <button class="player-chip" id="profile-action" type="button">Player: Guest</button>
              <div class="mode-actions">
                <p class="mode-actions__kicker">Main game · 10 levels</p>
                <button class="button button--primary" id="tour-action" type="button" disabled>Start the 10-Level Tour</button>
                <p class="mode-actions__note">Begin at Level 1 and unlock every cosmic challenge.</p>
                <button class="quick-play-link" id="primary-action" type="button" aria-describedby="quick-play-note" disabled>${escapeHtml(COPY.play)}</button>
                <small id="quick-play-note">One continuous run · no levels</small>
              </div>
              <div class="menu-links">
                <button class="button button--quiet" id="records-action" type="button">My Records</button>
                <button class="button button--quiet" id="menu-settings" type="button">${escapeHtml(ui.common.settings)}</button>
              </div>
              <p class="loading-copy" id="loading-copy" role="status">${escapeHtml(ui.menu.loading)}</p>
            </section>

            <section class="screen screen--tour" id="tour-screen" aria-labelledby="tour-title" hidden>
              <button class="tour-back" id="tour-back" type="button">← Launch bay</button>
              <p class="eyebrow">10-level adventure</p>
              <h2 id="tour-title">Cosmic Clog Tour</h2>
              <div class="tour-progress-line" aria-label="Tour progress">
                <span id="tour-progress-level">Level 1 of 10</span>
                <div class="tour-progress-track" id="tour-progress-track" role="progressbar" aria-label="Tour completion" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0"><span id="tour-progress-bar"></span></div>
                <span class="tour-progress" id="tour-progress">0% complete</span>
              </div>
              <section class="next-mission" aria-labelledby="next-mission-title">
                <div class="next-mission__heading">
                  <div>
                    <p class="eyebrow" id="next-mission-kicker">Your next mission</p>
                    <h3 id="next-mission-title">Fresh Fixtures</h3>
                    <p id="next-mission-description">Glide, aim, and learn the three-hit clog.</p>
                  </div>
                  <span class="next-mission__stars" id="next-mission-stars" aria-label="Three stars available">☆ ☆ ☆</span>
                </div>
                <button class="button button--primary tour-play" id="continue-tour-action" type="button">Play Level 1</button>
              </section>
              <div class="selected-launcher">
                <div><span>Your launcher</span><strong id="selected-launcher-name">Poop Popper · Selected</strong></div>
                <button class="tour-detail-action" id="change-launcher-action" type="button" aria-expanded="false" aria-controls="launcher-panel">Change launcher</button>
              </div>
              <section class="tour-detail-panel" id="launcher-panel" aria-label="Launcher collection" hidden>
                <fieldset class="weapon-picker" aria-describedby="launcher-help">
                  <legend>Launcher collection</legend>
                  <p class="launcher-help" id="launcher-help">Bubble unlocks at 10 Tour clogs. Golden Plunger unlocks at 25.</p>
                  <div id="weapon-options"></div>
                </fieldset>
              </section>
              <label class="comfy-choice" id="comfy-choice" hidden><input id="comfy-mode" type="checkbox" /> Comfy Mode: +1 heart and gentler speed</label>
              <div class="tour-secondary-actions">
                <button class="tour-detail-action" id="view-levels-action" type="button" aria-expanded="false" aria-controls="tour-route-panel">View all 10 levels</button>
                <button class="tour-detail-action" id="view-rewards-action" type="button" aria-expanded="false" aria-controls="tour-rewards-panel">Tour rewards</button>
              </div>
              <section class="tour-route tour-detail-panel" id="tour-route-panel" aria-labelledby="tour-route-title" hidden>
                <h3 id="tour-route-title">Your 10-level route</h3>
                <p>Levels unlock in this exact order. Tap any available level to replay it.</p>
                <div class="level-map" id="level-map" aria-label="Ten campaign levels in order"></div>
              </section>
              <section class="tour-rewards tour-detail-panel" id="tour-rewards-panel" aria-labelledby="tour-rewards-title" hidden>
                <h3 id="tour-rewards-title">Tour rewards</h3>
                <ul>
                  <li><strong>10 Tour clogs</strong><span>Bubble Blaster</span></li>
                  <li><strong>25 Tour clogs</strong><span>Golden Plunger Cannon</span></li>
                  <li><strong>Beat Level 8</strong><span>Cosmic Mint accent</span></li>
                  <li><strong>Beat Level 10</strong><span>Tour champion badge</span></li>
                </ul>
              </section>
            </section>

            <section class="screen screen--pause" id="pause-screen" aria-labelledby="pause-title" hidden>
              <p class="eyebrow">${escapeHtml(ui.pause.kicker)}</p>
              <h2 id="pause-title">${escapeHtml(ui.pause.title)}</h2>
              <p id="pause-reason">${escapeHtml(ui.pause.defaultReason)}</p>
              <button class="button button--primary" id="resume-action" type="button">${escapeHtml(ui.pause.resume)}</button>
              <button class="button button--ghost" id="pause-settings" type="button">${escapeHtml(ui.common.settings)}</button>
              <button class="button button--quiet" id="restart-action" type="button">${escapeHtml(ui.pause.restart)}</button>
              <button class="button button--quiet" id="pause-home" type="button">Exit to launch bay</button>
            </section>

            <section class="screen screen--result" id="result-screen" aria-labelledby="result-heading" hidden>
              <div class="result-badge" id="result-badge" aria-hidden="true">✨</div>
              <p class="eyebrow" id="result-kicker">${escapeHtml(ui.results.runComplete)}</p>
              <h2 id="result-heading">${escapeHtml(COPY.victoryTitle)}</h2>
              <p class="result-title" id="result-title">${escapeHtml(COPY.titles.champion)}</p>
              <p class="result-stars" id="result-stars" aria-label="No stars earned"></p>
              <dl class="result-grid">
                <div><dt>${escapeHtml(ui.common.score)}</dt><dd id="result-score">0</dd></div>
                <div><dt>${escapeHtml(ui.results.clogged)}</dt><dd id="result-clogs">0</dd></div>
                <div><dt>${escapeHtml(ui.results.bestCombo)}</dt><dd id="result-combo">0×</dd></div>
                <div><dt>${escapeHtml(ui.results.heartsLeft)}</dt><dd id="result-lives">0</dd></div>
                <div class="result-grid__wide"><dt>${escapeHtml(ui.results.finalWeapon)}</dt><dd id="result-weapon">${escapeHtml(COPY.weapons.poop)}</dd></div>
              </dl>
              <p class="best-score" id="best-score" hidden>${escapeHtml(ui.results.newBest)}</p>
              <p class="unlock-note" id="unlock-note" hidden></p>
              <button class="button button--ghost" id="replay-action" type="button">${escapeHtml(COPY.replay)}</button>
              <div class="result-links">
                <button class="button button--quiet" id="share-action" type="button">Share result</button>
                <button class="button button--quiet" id="result-home" type="button">Launch bay</button>
                <button class="button button--quiet" id="result-settings" type="button">${escapeHtml(ui.common.settings)}</button>
              </div>
            </section>
          </div>

          <div class="mobile-tip" aria-hidden="true"><span>↔</span> ${escapeHtml(ui.controls.mobileTip)}</div>
          <div class="game-actions">
            <button class="tutorial-skip" id="tutorial-skip" type="button" hidden>${escapeHtml(ui.controls.skipTutorial)}</button>
            <button class="power-button" id="power-action" type="button" aria-label="${escapeHtml(ui.controls.activateTriple)}" hidden>🍨 <span>${escapeHtml(ui.controls.scoop)}</span></button>
            <button class="icon-button" id="pause-action" type="button" aria-label="${escapeHtml(ui.controls.pause)}" hidden>Ⅱ</button>
            <button class="icon-button" id="settings-action" type="button" aria-label="${escapeHtml(ui.controls.openSettings)}">⚙</button>
          </div>
        </section>

        <aside class="side-panel side-panel--right" aria-label="${escapeHtml(ui.aria.currentRunDetails)}">
          <div class="score-card"><span>${escapeHtml(ui.common.score)}</span><strong id="side-score">000000</strong></div>
          <div class="combo-card"><span>${escapeHtml(ui.common.combo)}</span><strong id="side-combo">1×</strong></div>
          <div class="tip-card"><span aria-hidden="true">✨</span><p>${escapeHtml(ui.sidePanel.milestone)}</p></div>
          <div class="privacy-note">${escapeHtml(ui.sidePanel.privacy)}</div>
        </aside>
      </main>

      <dialog class="settings-dialog" id="settings-dialog" aria-labelledby="settings-title">
        <form method="dialog">
          <div class="dialog-heading"><div><p class="eyebrow">${escapeHtml(ui.settings.kicker)}</p><h2 id="settings-title">${escapeHtml(ui.common.settings)}</h2></div><button class="close-button" value="close" aria-label="${escapeHtml(ui.settings.close)}">×</button></div>
          <fieldset><legend>${escapeHtml(ui.settings.soundAndFeel)}</legend>
            <label class="toggle"><span>${escapeHtml(ui.settings.music)}</span><input id="setting-music" type="checkbox" /></label>
            <label class="toggle"><span>${escapeHtml(ui.settings.effects)}</span><input id="setting-effects" type="checkbox" /></label>
            <label class="toggle"><span>${escapeHtml(ui.settings.haptics)}</span><input id="setting-haptics" type="checkbox" /></label>
            <label class="toggle"><span>${escapeHtml(ui.settings.muteAll)}</span><input id="setting-mute" type="checkbox" /></label>
            <label class="toggle"><span>${escapeHtml(ui.settings.reducedMotion)}</span><input id="setting-motion" type="checkbox" /></label>
          </fieldset>
          <fieldset><legend>${escapeHtml(ui.settings.controls)}</legend>
            <label class="range-label" for="setting-sensitivity"><span>${escapeHtml(ui.settings.dragSensitivity)}</span><output id="sensitivity-output">100%</output></label>
            <input id="setting-sensitivity" type="range" min="0.6" max="1.6" step="0.1" />
            <label class="range-label" for="setting-tilt-sensitivity"><span>${escapeHtml(ui.settings.tiltSensitivity)}</span><output id="tilt-sensitivity-output">100%</output></label>
            <input id="setting-tilt-sensitivity" type="range" min="0.6" max="1.6" step="0.1" />
            <div class="tilt-row"><div><strong>${escapeHtml(ui.settings.optionalTilt)}</strong><small id="tilt-status">${escapeHtml(ui.common.touchAvailable)}</small></div><button class="button button--mini" id="tilt-action" type="button">${escapeHtml(ui.settings.enableTilt)}</button></div>
            <div class="tilt-row"><div><strong>${escapeHtml(ui.settings.visualTutorial)}</strong><small id="tutorial-status">${escapeHtml(ui.settings.tutorialFirst)}</small></div><button class="button button--mini" id="tutorial-action" type="button">${escapeHtml(ui.settings.replayNext)}</button></div>
          </fieldset>
          <button class="button button--primary button--full" value="close">${escapeHtml(ui.common.done)}</button>
          <button class="button button--danger button--full" id="erase-progress" type="button">Erase player progress</button>
        </form>
      </dialog>

      <dialog class="settings-dialog profile-dialog" id="profile-dialog" aria-labelledby="profile-title">
        <form method="dialog" id="profile-form">
          <div class="dialog-heading"><div><p class="eyebrow">Saved on this device</p><h2 id="profile-title">Who is playing?</h2></div><button class="close-button" id="close-profile" type="button" aria-label="Close profile">×</button></div>
          <p class="dialog-copy">Add a nickname to personalize your records. No email or account is required.</p>
          <label class="nickname-label" for="nickname-input">Nickname <span>2–16 characters</span></label>
          <input class="nickname-input" id="nickname-input" type="text" minlength="2" maxlength="16" autocomplete="nickname" placeholder="Guest" />
          <p class="form-error" id="nickname-error" role="alert" hidden>Please use 2–16 visible characters.</p>
          <button class="button button--primary button--full" id="save-profile" type="submit">Save player</button>
          <button class="button button--ghost button--full" id="use-guest" type="button">Play as Guest</button>
        </form>
      </dialog>

      <dialog class="settings-dialog records-dialog" id="records-dialog" aria-labelledby="records-title">
        <form method="dialog">
          <div class="dialog-heading"><div><p class="eyebrow">Personal bests</p><h2 id="records-title">My Records</h2></div><button class="close-button" value="close" aria-label="Close records">×</button></div>
          <div class="records-summary" id="records-summary"></div>
          <div class="records-levels" id="records-levels"></div>
          <h3>Recent runs</h3>
          <ol class="recent-runs" id="recent-runs"></ol>
          <p class="dialog-copy">Records live only in this browser on this device.</p>
          <button class="button button--primary button--full" value="close">Done</button>
        </form>
      </dialog>

      <div class="toast" id="toast" role="status" aria-live="polite" hidden></div>
      <div class="sr-only" id="live-region" role="status" aria-live="polite"></div>
    `;
  }

  private createGame(): void {
    this.game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: "game-container",
      width: GAME_WIDTH,
      height: GAME_HEIGHT,
      // Keep a fixed logical backing store; CSS performs responsive scaling so
      // very high device-pixel ratios cannot create an unbounded canvas.
      transparent: true,
      backgroundColor: "#160d2a",
      render: {
        antialias: true,
        roundPixels: true,
        powerPreference: "high-performance",
      },
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
        width: GAME_WIDTH,
        height: GAME_HEIGHT,
      },
      physics: {
        default: "arcade",
        arcade: { gravity: { x: 0, y: 0 }, debug: false },
      },
      scene: [BootScene, GameScene],
      input: { activePointers: 2 },
      banner: false,
    });
    this.game.registry.set("preferences", this.preferences);
    this.game.registry.set("bestScore", this.preferences.bestScore);
    this.game.registry.set("session", this.activeSession);
  }

  private bindUi(): void {
    required<HTMLButtonElement>("#primary-action").addEventListener(
      "click",
      () => this.startQuickPlay(),
    );
    required<HTMLButtonElement>("#tour-action").addEventListener("click", () =>
      this.showTour(),
    );
    required<HTMLButtonElement>("#tour-back").addEventListener("click", () =>
      this.showMenu(),
    );
    TOUR_PANEL_CONTROLS.forEach(([buttonSelector, panelSelector]) =>
      required<HTMLButtonElement>(buttonSelector).addEventListener(
        "click",
        () => this.toggleTourPanel(buttonSelector, panelSelector),
      ),
    );
    required<HTMLButtonElement>("#continue-tour-action").addEventListener(
      "click",
      (event) => {
        const level = getCampaignLevel(
          Number(
            (event.currentTarget as HTMLButtonElement).dataset.recommendedLevel,
          ),
        );
        if (level && isLevelUnlocked(this.progress, level.id))
          this.startCampaign(
            level,
            required<HTMLInputElement>("#comfy-mode").checked,
          );
      },
    );
    required<HTMLButtonElement>("#profile-action").addEventListener(
      "click",
      () => this.openProfile(),
    );
    required<HTMLButtonElement>("#records-action").addEventListener(
      "click",
      () => this.openRecords(),
    );
    required<HTMLButtonElement>("#replay-action").addEventListener(
      "click",
      () => this.startRun(this.activeSession),
    );
    required<HTMLButtonElement>("#result-home").addEventListener("click", () =>
      this.showMenu(),
    );
    required<HTMLButtonElement>("#share-action").addEventListener(
      "click",
      () => void this.shareLastResult(),
    );
    required<HTMLButtonElement>("#resume-action").addEventListener(
      "click",
      () => this.resume(),
    );
    required<HTMLButtonElement>("#pause-action").addEventListener("click", () =>
      this.pause("manual"),
    );
    required<HTMLButtonElement>("#power-action").addEventListener("click", () =>
      this.game.events.emit("power:activate"),
    );
    required<HTMLButtonElement>("#tutorial-skip").addEventListener(
      "click",
      () => this.game.events.emit("tutorial:skip"),
    );
    required<HTMLButtonElement>("#restart-action").addEventListener(
      "click",
      () => {
        if (globalThis.confirm(COPY.ui.pause.restartConfirm))
          this.startRun(this.activeSession);
      },
    );
    required<HTMLButtonElement>("#pause-home").addEventListener("click", () =>
      this.exitRunToMenu(),
    );
    [
      "#menu-settings",
      "#pause-settings",
      "#result-settings",
      "#settings-action",
    ].forEach((selector) =>
      required<HTMLButtonElement>(selector).addEventListener("click", () =>
        this.openSettings(),
      ),
    );

    const settings = required<HTMLDialogElement>("#settings-dialog");
    settings.addEventListener("close", () => {
      if (this.settingsShouldResume && this.playing && this.paused)
        this.resume();
      this.settingsShouldResume = false;
    });
    settings.addEventListener("click", (event) => {
      if (event.target === settings) settings.close();
    });

    required<HTMLFormElement>("#profile-form").addEventListener(
      "submit",
      (event) => {
        event.preventDefault();
        this.saveProfile();
      },
    );
    required<HTMLButtonElement>("#close-profile").addEventListener(
      "click",
      () => required<HTMLDialogElement>("#profile-dialog").close(),
    );
    required<HTMLButtonElement>("#use-guest").addEventListener("click", () => {
      this.progress = setNickname(this.progress, null);
      this.saveProgress();
      required<HTMLDialogElement>("#profile-dialog").close();
    });
    required<HTMLButtonElement>("#erase-progress").addEventListener(
      "click",
      () => this.eraseProgress(),
    );

    const checkboxMap: Array<[string, keyof Preferences]> = [
      ["#setting-music", "music"],
      ["#setting-effects", "effects"],
      ["#setting-haptics", "haptics"],
      ["#setting-mute", "muteAll"],
      ["#setting-motion", "reducedMotion"],
    ];
    checkboxMap.forEach(([selector, key]) => {
      required<HTMLInputElement>(selector).addEventListener(
        "change",
        (event) => {
          this.updatePreferences({
            [key]: (event.currentTarget as HTMLInputElement).checked,
          });
        },
      );
    });
    required<HTMLInputElement>("#setting-sensitivity").addEventListener(
      "input",
      (event) => {
        this.updatePreferences({
          sensitivity: Number((event.currentTarget as HTMLInputElement).value),
        });
      },
    );
    required<HTMLInputElement>("#setting-tilt-sensitivity").addEventListener(
      "input",
      (event) => {
        this.updatePreferences({
          tiltSensitivity: Number(
            (event.currentTarget as HTMLInputElement).value,
          ),
        });
      },
    );
    required<HTMLButtonElement>("#tilt-action").addEventListener(
      "click",
      () => void this.toggleTilt(),
    );
    required<HTMLButtonElement>("#tutorial-action").addEventListener(
      "click",
      () => {
        this.updatePreferences({ tutorialComplete: false });
        required<HTMLElement>("#tutorial-status").textContent =
          COPY.ui.settings.tutorialReady;
      },
    );
    document.addEventListener("visibilitychange", () => {
      if (document.hidden && this.playing && !this.paused) this.pause("focus");
    });
    globalThis.addEventListener("keydown", (event) => {
      if (event.code !== "Space" || !this.playing || this.paused) return;
      event.preventDefault();
      this.game.events.emit("power:activate");
    });
  }

  private bindGameEvents(): void {
    globalThis.addEventListener(EVENT.gameReady, () => {
      this.gameReady = true;
      const button = required<HTMLButtonElement>("#primary-action");
      button.disabled = false;
      required<HTMLButtonElement>("#tour-action").disabled = false;
      required<HTMLElement>("#loading-copy").textContent = COPY.ui.menu.ready;
    });
    globalThis.addEventListener(EVENT.hud, ((event: CustomEvent<HudDetail>) =>
      this.renderHud(event.detail)) as EventListener);
    globalThis.addEventListener(EVENT.result, ((
      event: CustomEvent<ResultDetail>,
    ) => this.showResult(event.detail)) as EventListener);
    globalThis.addEventListener(EVENT.announce, ((event: CustomEvent<string>) =>
      this.announce(event.detail)) as EventListener);

    this.game.events.on(
      "audio:effect",
      (name: Parameters<AudioDirector["effect"]>[0]) => this.audio.effect(name),
    );
    this.game.events.on("audio:haptic", (pattern: number | number[]) =>
      this.audio.haptic(pattern),
    );
    this.game.events.on("tutorial:complete", () =>
      this.updatePreferences({ tutorialComplete: true }),
    );
    this.game.events.on("tutorial:visibility", (visible: boolean) => {
      this.tutorialVisible = visible;
      this.syncTutorialControl();
    });
  }

  private startQuickPlay(): void {
    this.startRun({
      mode: "quick",
      level: null,
      selectedWeapon: "poop",
      comfyMode: false,
      bestScore: this.progress.quickPlayBest,
      continued: false,
    });
  }

  private startCampaign(
    level: CampaignLevel,
    comfyMode = false,
    continued = false,
    preserveTransition = false,
  ): void {
    if (!isLevelUnlocked(this.progress, level.id)) return;
    const loanedWeapon =
      level.id === 3 && !this.progress.unlockedWeapons.includes("bubble")
        ? "bubble"
        : level.id === 7 && !this.progress.unlockedWeapons.includes("plunger")
          ? "plunger"
          : this.progress.selectedWeapon;
    this.startRun(
      {
        mode: "campaign",
        level,
        selectedWeapon: loanedWeapon,
        comfyMode: comfyMode && comfyModeAvailable(this.progress, level.id),
        bestScore:
          this.progress.levelRecords[level.id.toString()]?.bestScore ?? 0,
        continued,
      },
      preserveTransition,
    );
  }

  private startRun(session: GameSession, preserveTransition = false): void {
    if (!this.gameReady) return;
    if (!preserveTransition) this.clearCampaignTransition();
    this.activeSession = session;
    this.audio.effect("click");
    this.audio.startMusic(session.level?.id ?? null);
    this.playing = true;
    this.paused = false;
    required<HTMLElement>(".app-shell").classList.remove("app-shell--tour");
    required<HTMLElement>(".game-column").classList.add("game-column--playing");
    required<HTMLElement>(".game-column").classList.remove(
      "game-column--paused",
    );
    this.tutorialVisible = false;
    this.clearBonusGuide();
    this.hideAllScreens();
    required<HTMLElement>("#hud").hidden = false;
    required<HTMLButtonElement>("#pause-action").hidden = false;
    this.syncTutorialControl();
    this.game.registry.set("preferences", this.preferences);
    this.game.registry.set("bestScore", this.preferences.bestScore);
    this.game.registry.set("session", session);
    const current = this.game.scene.getScene("game");
    if (current.scene.isActive() || current.scene.isPaused())
      current.scene.restart();
    else this.game.scene.start("game");
    required<HTMLElement>("#game-container").focus({ preventScroll: true });
  }

  private pause(reason: "manual" | "focus" | "settings" = "manual"): void {
    if (!this.playing || this.paused) return;
    this.paused = true;
    required<HTMLElement>(".game-column").classList.add("game-column--paused");
    this.audio.stopMusic();
    this.game.scene.pause("game");
    required<HTMLElement>("#pause-reason").textContent =
      reason === "focus"
        ? COPY.ui.pause.focusReason
        : reason === "settings"
          ? COPY.ui.pause.settingsReason
          : COPY.ui.pause.manualReason;
    required<HTMLElement>("#pause-screen").hidden = false;
    required<HTMLButtonElement>("#resume-action").focus();
    this.syncTutorialControl();
    this.announce(
      reason === "focus"
        ? COPY.ui.pause.focusAnnouncement
        : COPY.ui.pause.pausedAnnouncement,
    );
  }

  private resume(): void {
    if (!this.playing || !this.paused) return;
    this.paused = false;
    required<HTMLElement>(".game-column").classList.remove(
      "game-column--paused",
    );
    this.audio.startMusic(this.activeSession.level?.id ?? null);
    required<HTMLElement>("#pause-screen").hidden = true;
    this.game.scene.resume("game");
    required<HTMLButtonElement>("#pause-action").focus();
    this.syncTutorialControl();
    this.announce(COPY.ui.pause.resumedAnnouncement);
  }

  private openSettings(): void {
    this.settingsShouldResume = this.playing && !this.paused;
    if (this.settingsShouldResume) this.pause("settings");
    this.syncSettings();
    required<HTMLDialogElement>("#settings-dialog").showModal();
  }

  private renderHud(detail: HudDetail): void {
    const score = detail.score.toString().padStart(6, "0");
    required<HTMLElement>("#hud-score").textContent = score;
    required<HTMLElement>("#side-score").textContent = score;
    required<HTMLElement>("#side-combo").textContent =
      `${Math.max(1, detail.combo)}×`;
    const level = this.activeSession.level;
    required<HTMLElement>("#hud-wave").textContent =
      this.activeSession.mode === "campaign" && level
        ? `Tour · Level ${level.id} / ${CAMPAIGN_LEVELS.length} · ${level.name}`
        : `Quick Play · ${detail.wave}`;
    required<HTMLElement>("#hud-clogs").textContent =
      `${detail.clogs} ${COPY.ui.hud.clogged} · ${detail.combo}×`;
    const weapon = COPY.weapons[detail.weapon];
    required<HTMLElement>("#hud-power").textContent = detail.powerUp
      ? `${weapon} · ${detail.powerUp}`
      : weapon;
    required<HTMLButtonElement>("#power-action").hidden =
      detail.heldPowerUp !== "triple";
    if (detail.heldPowerUp === "triple") {
      this.showBonusGuide("triple-ready", `🍨 ${COPY.powerUps.tripleGuide}`);
    } else if (detail.tripleUntil > 0 && detail.powerUp?.includes("Triple")) {
      this.showBonusGuide(
        "triple-active",
        `✨ ${COPY.powerUps.tripleActiveGuide}`,
      );
    } else if (detail.shield > 0) {
      this.showBonusGuide(
        `shield-${detail.shield}`,
        `🧻 ${COPY.powerUps.shieldGuide}`,
      );
    } else {
      this.clearBonusGuide();
    }
    const lives = "🧻♥ ".repeat(detail.lives).trim() || "—";
    const livesElement = required<HTMLElement>("#hud-lives");
    livesElement.textContent = lives;
    livesElement.setAttribute(
      "aria-label",
      `${detail.lives} ${detail.lives === 1 ? COPY.ui.aria.life : COPY.ui.aria.lives}`,
    );
  }

  private showBonusGuide(state: string, message: string): void {
    if (this.bonusGuideState === state) return;
    this.clearBonusGuide(false);
    const guide = required<HTMLElement>("#bonus-guide");
    this.bonusGuideState = state;
    guide.textContent = message;
    guide.hidden = false;
    this.bonusGuideTimer = globalThis.setTimeout(() => {
      if (this.bonusGuideState === state) guide.hidden = true;
      this.bonusGuideTimer = null;
    }, 2_400);
  }

  private clearBonusGuide(resetState = true): void {
    if (this.bonusGuideTimer !== null)
      globalThis.clearTimeout(this.bonusGuideTimer);
    this.bonusGuideTimer = null;
    if (resetState) this.bonusGuideState = null;
    const guide = document.querySelector<HTMLElement>("#bonus-guide");
    if (guide) guide.hidden = true;
  }

  private showResult(detail: ResultDetail): void {
    const victory = detail.state.result === "victory";
    const resultLevel =
      detail.mode === "campaign" && detail.levelId !== null
        ? getCampaignLevel(detail.levelId)
        : null;
    if (resultLevel) {
      // The result event is authoritative. Keep replay pinned to the level that
      // actually ended, even if a prior transition or scene restart was pending.
      this.activeSession = {
        ...this.activeSession,
        level: resultLevel,
        continued: false,
        bestScore:
          this.progress.levelRecords[resultLevel.id.toString()]?.bestScore ?? 0,
      };
    }
    const recorded = recordRun(this.progress, {
      mode: detail.mode,
      level: resultLevel,
      score: detail.state.score,
      clogs: detail.state.clogs,
      lives: detail.state.lives,
      weapon: detail.state.weapon,
      result: victory ? "victory" : "defeat",
    });
    this.progress = recorded.progress;
    this.lastRun = this.progress.recentRuns[0] ?? null;
    this.saveProgress();
    const nextLevel =
      detail.mode === "campaign" &&
      victory &&
      detail.levelId !== null &&
      detail.levelId < CAMPAIGN_LEVELS.length
        ? getCampaignLevel(detail.levelId + 1)
        : null;
    if (nextLevel) {
      this.showCampaignTransition(detail, recorded, nextLevel);
      return;
    }

    this.clearCampaignTransition();
    this.playing = false;
    this.paused = false;
    required<HTMLElement>(".game-column").classList.remove(
      "game-column--playing",
      "game-column--paused",
    );
    this.tutorialVisible = false;
    this.audio.stopMusic();
    required<HTMLElement>("#hud").hidden = true;
    required<HTMLButtonElement>("#pause-action").hidden = true;
    required<HTMLButtonElement>("#power-action").hidden = true;
    this.clearBonusGuide();
    this.syncTutorialControl();
    required<HTMLElement>("#result-screen").hidden = false;
    required<HTMLElement>("#result-badge").textContent = victory ? "🚀" : "🫧";
    required<HTMLElement>("#result-kicker").textContent = victory
      ? detail.mode === "campaign"
        ? `Tour · Level ${detail.levelId ?? "?"} of ${CAMPAIGN_LEVELS.length} complete`
        : COPY.ui.results.victoryKicker
      : COPY.ui.results.defeatKicker;
    required<HTMLElement>("#result-heading").textContent = victory
      ? COPY.victoryTitle
      : COPY.defeatTitle;
    required<HTMLElement>("#result-title").textContent = detail.title;
    required<HTMLElement>("#result-score").textContent =
      detail.state.score.toLocaleString();
    required<HTMLElement>("#result-clogs").textContent =
      detail.state.clogs.toString();
    required<HTMLElement>("#result-combo").textContent =
      `${detail.state.bestCombo}×`;
    required<HTMLElement>("#result-lives").textContent =
      detail.state.lives.toString();
    required<HTMLElement>("#result-weapon").textContent =
      COPY.weapons[detail.state.weapon];
    const stars = required<HTMLElement>("#result-stars");
    stars.hidden = detail.mode !== "campaign";
    stars.textContent = `${"★".repeat(recorded.stars)}${"☆".repeat(3 - recorded.stars)}`;
    stars.setAttribute("aria-label", `${recorded.stars} of 3 stars earned`);
    required<HTMLElement>("#best-score").hidden = !recorded.newBest;
    if (detail.mode === "quick" && recorded.newBest) {
      this.updatePreferences({ bestScore: detail.state.score });
      this.game.registry.set("bestScore", detail.state.score);
    }
    const unlockNote = required<HTMLElement>("#unlock-note");
    unlockNote.hidden = !recorded.newWeapon;
    unlockNote.textContent = recorded.newWeapon
      ? `${COPY.weapons[recorded.newWeapon]} unlocked for the Tour!`
      : "";
    required<HTMLButtonElement>("#replay-action").textContent =
      detail.mode === "campaign" ? "Replay level" : COPY.replay;
    globalThis.setTimeout(
      () => required<HTMLButtonElement>("#replay-action").focus(),
      100,
    );
    this.announce(
      `${victory ? COPY.victoryTitle : COPY.defeatTitle} ${detail.title}. Score ${detail.state.score}.`,
    );
  }

  private showCampaignTransition(
    detail: ResultDetail,
    recorded: ReturnType<typeof recordRun>,
    nextLevel: CampaignLevel,
  ): void {
    this.clearCampaignTransition();
    this.paused = false;
    this.tutorialVisible = false;
    this.clearBonusGuide();
    this.syncTutorialControl();
    required<HTMLButtonElement>("#pause-action").hidden = true;
    required<HTMLButtonElement>("#power-action").hidden = true;
    required<HTMLElement>("#result-screen").hidden = true;

    const transition = required<HTMLElement>("#level-transition");
    required<HTMLElement>("#transition-cleared").textContent =
      `Level ${detail.levelId ?? "?"} cleared!`;
    required<HTMLElement>("#transition-next").textContent =
      `Next · Level ${nextLevel.id} of ${CAMPAIGN_LEVELS.length}`;
    required<HTMLElement>("#transition-name").textContent = nextLevel.name;
    required<HTMLElement>("#transition-pace").textContent =
      `Fleet speed · ${nextLevel.speedMultiplier.toFixed(2)}×`;
    const stars = required<HTMLElement>("#transition-stars");
    stars.textContent = `${"★".repeat(recorded.stars)}${"☆".repeat(3 - recorded.stars)}`;
    stars.setAttribute("aria-label", `${recorded.stars} of 3 stars earned`);
    const reward = required<HTMLElement>("#transition-reward");
    reward.hidden = !recorded.newWeapon;
    reward.textContent = recorded.newWeapon
      ? `${COPY.weapons[recorded.newWeapon]} unlocked!`
      : "";
    transition.hidden = false;

    const comfyMode = this.activeSession.comfyMode;
    this.announce(
      `Level ${detail.levelId ?? "?"} cleared. Next is level ${nextLevel.id} of ${CAMPAIGN_LEVELS.length}, ${nextLevel.name}.`,
    );
    this.startCampaign(nextLevel, comfyMode, true, true);
    this.campaignTransitionTimer = globalThis.setTimeout(() => {
      this.campaignTransitionTimer = null;
      transition.hidden = true;
    }, CAMPAIGN_TRANSITION_MS);
    if (document.hidden) this.pause("focus");
  }

  private clearCampaignTransition(): void {
    if (this.campaignTransitionTimer !== null)
      globalThis.clearTimeout(this.campaignTransitionTimer);
    this.campaignTransitionTimer = null;
    const transition = document.querySelector<HTMLElement>("#level-transition");
    if (transition) transition.hidden = true;
  }

  private showMenu(): void {
    this.clearCampaignTransition();
    this.playing = false;
    this.paused = false;
    this.audio.stopMusic();
    this.clearBonusGuide();
    required<HTMLElement>(".app-shell").classList.remove("app-shell--tour");
    required<HTMLElement>(".game-column").classList.remove(
      "game-column--playing",
      "game-column--paused",
    );
    this.hideAllScreens();
    required<HTMLElement>("#hud").hidden = true;
    required<HTMLButtonElement>("#pause-action").hidden = true;
    required<HTMLElement>("#menu-screen").hidden = false;
    this.syncPlayerUi();
    this.applyPendingUpdateAtLaunchBay();
    required<HTMLButtonElement>("#tour-action").focus();
  }

  private exitRunToMenu(): void {
    this.clearCampaignTransition();
    const current = this.game.scene.getScene("game");
    if (current.scene.isActive() || current.scene.isPaused())
      this.game.scene.stop("game");
    this.showMenu();
    this.announce(
      "Returned to the launch bay. Your completed Tour levels are saved.",
    );
  }

  private showTour(): void {
    this.hideAllScreens();
    required<HTMLElement>(".app-shell").classList.add("app-shell--tour");
    required<HTMLElement>(".game-column").classList.remove(
      "game-column--playing",
      "game-column--paused",
    );
    this.collapseTourPanels();
    required<HTMLElement>("#tour-screen").hidden = false;
    this.renderTour();
    required<HTMLButtonElement>("#continue-tour-action").focus();
  }

  private renderTour(): void {
    const percent = completionPercent(this.progress);
    required<HTMLElement>("#tour-progress").textContent =
      `${percent}% complete`;
    required<HTMLElement>("#tour-progress-bar").style.width = `${percent}%`;
    const progressTrack = required<HTMLElement>("#tour-progress-track");
    progressTrack.setAttribute("aria-valuenow", percent.toString());
    const recommendedId = Math.min(
      CAMPAIGN_LEVELS.length,
      this.progress.highestUnlockedLevel,
    );
    const recommended = getCampaignLevel(recommendedId);
    const isReplay = this.progress.campaignComplete;
    const hasFailed =
      (this.progress.failureStreaks[recommendedId.toString()] ?? 0) > 0;
    required<HTMLElement>("#next-mission-kicker").textContent = isReplay
      ? "Tour complete · Replay any mission"
      : hasFailed
        ? "Ready for another try"
        : "Your next mission";
    required<HTMLElement>("#next-mission-title").textContent = recommended.name;
    required<HTMLElement>("#tour-progress-level").textContent =
      `Level ${recommended.id} of ${CAMPAIGN_LEVELS.length}`;
    required<HTMLElement>("#next-mission-description").textContent =
      recommended.description;
    const playButton = required<HTMLButtonElement>("#continue-tour-action");
    playButton.dataset.recommendedLevel = recommended.id.toString();
    playButton.textContent = `${isReplay ? "Replay" : hasFailed ? "Try again" : "Play"} Level ${recommended.id}`;
    const recommendedRecord =
      this.progress.levelRecords[recommended.id.toString()];
    const recommendedStars = recommendedRecord?.stars ?? 0;
    const stars = required<HTMLElement>("#next-mission-stars");
    stars.textContent = `${"★".repeat(recommendedStars)}${"☆".repeat(3 - recommendedStars)}`;
    stars.setAttribute(
      "aria-label",
      recommendedStars > 0
        ? `${recommendedStars} of 3 stars earned`
        : "Three stars available",
    );

    const map = required<HTMLElement>("#level-map");
    map.innerHTML = CAMPAIGN_LEVELS.map((level) => {
      const record = this.progress.levelRecords[level.id.toString()];
      const unlocked = isLevelUnlocked(this.progress, level.id);
      const stars = record?.stars ?? 0;
      const status = !unlocked
        ? `Locked · Beat Level ${level.id - 1}`
        : record?.completions
          ? `${"★".repeat(stars)}${"☆".repeat(3 - stars)} · Replay`
          : level.id === recommendedId
            ? "Next mission · Ready"
            : "Available";
      const stateClass = record?.completions
        ? "level-node--done"
        : level.id === recommendedId
          ? "level-node--next"
          : "";
      return `<button class="level-node ${stateClass}" type="button" data-level="${level.id}" ${unlocked ? "" : "disabled"} aria-label="Level ${level.id}, ${escapeHtml(level.name)}, ${escapeHtml(status)}"><span>${level.id}</span><strong>${escapeHtml(level.name)}</strong><small>${escapeHtml(status)}</small></button>`;
    }).join("");
    map.querySelectorAll<HTMLButtonElement>("[data-level]").forEach((button) =>
      button.addEventListener("click", () => {
        const level = getCampaignLevel(Number(button.dataset.level));
        if (!level) return;
        const comfy = required<HTMLInputElement>("#comfy-mode").checked;
        this.startCampaign(level, comfy);
      }),
    );

    const weapons = required<HTMLElement>("#weapon-options");
    const weaponKinds: WeaponKind[] = ["poop", "bubble", "plunger"];
    const unlockTargets: Partial<Record<WeaponKind, number>> = {
      bubble: 10,
      plunger: 25,
    };
    weapons.innerHTML = weaponKinds
      .map((weapon) => {
        const unlocked = this.progress.unlockedWeapons.includes(weapon);
        const selected = this.progress.selectedWeapon === weapon;
        const target = unlockTargets[weapon];
        const status = unlocked
          ? selected
            ? "Selected"
            : "Available · Tap to choose"
          : `Locked · ${Math.min(this.progress.campaignClogs, target ?? 0)} / ${target ?? 0} Tour clogs`;
        return `<button class="weapon-option ${selected ? "weapon-option--selected" : ""}" data-weapon="${weapon}" type="button" ${unlocked ? "" : "disabled"} aria-label="${escapeHtml(`${COPY.weapons[weapon]}, ${status}`)}" aria-pressed="${selected}"><span>${unlocked ? "" : "🔒 "}${escapeHtml(COPY.weapons[weapon])}</span><small>${escapeHtml(status)}</small></button>`;
      })
      .join("");
    weapons
      .querySelectorAll<HTMLButtonElement>("[data-weapon]")
      .forEach((button) =>
        button.addEventListener("click", () => {
          this.progress = selectWeapon(
            this.progress,
            button.dataset.weapon as WeaponKind,
          );
          this.saveProgress();
          this.renderTour();
        }),
      );
    required<HTMLElement>("#selected-launcher-name").textContent =
      `${COPY.weapons[this.progress.selectedWeapon]} · Selected`;
    const comfy = comfyModeAvailable(this.progress, recommendedId);
    required<HTMLElement>("#comfy-choice").hidden = !comfy;
    if (!comfy) required<HTMLInputElement>("#comfy-mode").checked = false;
  }

  private collapseTourPanels(): void {
    TOUR_PANEL_CONTROLS.forEach(([buttonSelector, panelSelector]) => {
      required<HTMLButtonElement>(buttonSelector).setAttribute(
        "aria-expanded",
        "false",
      );
      required<HTMLElement>(panelSelector).hidden = true;
    });
  }

  private toggleTourPanel(buttonSelector: string, panelSelector: string): void {
    const panel = required<HTMLElement>(panelSelector);
    const willOpen = panel.hidden;
    this.collapseTourPanels();
    if (!willOpen) return;
    panel.hidden = false;
    required<HTMLButtonElement>(buttonSelector).setAttribute(
      "aria-expanded",
      "true",
    );
  }

  private openProfile(): void {
    const dialog = required<HTMLDialogElement>("#profile-dialog");
    const input = required<HTMLInputElement>("#nickname-input");
    input.value = this.progress.nickname ?? "";
    required<HTMLElement>("#nickname-error").hidden = true;
    dialog.showModal();
    input.focus();
  }

  private saveProfile(): void {
    const input = required<HTMLInputElement>("#nickname-input");
    const next = setNickname(this.progress, input.value);
    if (next === this.progress) {
      required<HTMLElement>("#nickname-error").hidden = false;
      input.focus();
      return;
    }
    this.progress = next;
    this.saveProgress();
    required<HTMLDialogElement>("#profile-dialog").close();
  }

  private openRecords(): void {
    const player = this.progress.nickname ?? "Guest";
    required<HTMLElement>("#records-summary").innerHTML =
      `<strong>${escapeHtml(player)}${this.progress.campaignComplete ? " · Cosmic Clog Champion" : ""}</strong><span>${completionPercent(this.progress)}% Tour · Level ${this.progress.highestUnlockedLevel} unlocked · ${this.progress.lifetimeClogs} lifetime clogs · Quick best ${this.progress.quickPlayBest.toLocaleString()} · ${escapeHtml(COPY.weapons[this.progress.selectedWeapon])}${this.progress.cosmeticAccentUnlocked ? " · Cosmic Mint accent" : ""}</span>`;
    required<HTMLElement>("#records-levels").innerHTML = CAMPAIGN_LEVELS.map(
      (level) => {
        const record = this.progress.levelRecords[level.id.toString()];
        return `<div><span>${level.id}. ${escapeHtml(level.name)}</span><strong>${record ? `${"★".repeat(record.stars)}${"☆".repeat(3 - record.stars)} · ${record.bestScore.toLocaleString()}` : "Not completed"}</strong></div>`;
      },
    ).join("");
    const recent = required<HTMLOListElement>("#recent-runs");
    recent.innerHTML = this.progress.recentRuns.length
      ? this.progress.recentRuns
          .map(
            (run) =>
              `<li><span>${escapeHtml(run.levelName)}</span><strong>${run.score.toLocaleString()} · ${run.result}</strong></li>`,
          )
          .join("")
      : "<li>No runs yet—your first score will appear here.</li>";
    required<HTMLDialogElement>("#records-dialog").showModal();
  }

  private async shareLastResult(): Promise<void> {
    if (!this.lastRun) return;
    const data = {
      player: this.progress.nickname ?? "Guest",
      run: this.lastRun,
      campaignComplete: this.progress.campaignComplete,
    };
    const text = shareText(data);
    try {
      const blob = await renderShareCard(data);
      const file = new File([blob], "buttroids-score.png", {
        type: "image/png",
      });
      const nav = navigator as Navigator & {
        canShare?: (data: ShareData) => boolean;
      };
      if (nav.share && nav.canShare?.({ files: [file] })) {
        await nav.share({ title: "Buttroids score", text, files: [file] });
        this.announce("Result shared.");
      } else {
        await navigator.clipboard?.writeText(text);
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "buttroids-score.png";
        link.click();
        URL.revokeObjectURL(link.href);
        this.announce("Score text copied and share card downloaded.");
      }
    } catch (error) {
      if ((error as DOMException).name !== "AbortError")
        this.announce(
          "Sharing is unavailable right now. Your record is saved.",
        );
    }
  }

  private saveProgress(): void {
    this.progress = this.progressStore.save(this.progress);
    this.syncPlayerUi();
  }

  private syncPlayerUi(): void {
    const player = this.progress.nickname ?? "Guest";
    required<HTMLButtonElement>("#profile-action").textContent =
      `Player: ${player}`;
    required<HTMLButtonElement>("#profile-action").classList.toggle(
      "player-chip--cosmic",
      this.progress.cosmeticAccentUnlocked,
    );
  }

  private eraseProgress(): void {
    if (!globalThis.confirm("Erase this player's nickname, Tour, and records?"))
      return;
    this.progress = this.progressStore.clear(0);
    this.updatePreferences({ bestScore: 0 });
    this.lastRun = null;
    this.saveProgress();
    this.settingsShouldResume = false;
    required<HTMLDialogElement>("#settings-dialog").close();
    this.showMenu();
    this.announce("Player progress erased from this device.");
  }

  private hideAllScreens(): void {
    ["#menu-screen", "#tour-screen", "#pause-screen", "#result-screen"].forEach(
      (selector) => (required<HTMLElement>(selector).hidden = true),
    );
  }

  private updatePreferences(patch: Partial<Preferences>): void {
    const musicWasEnabled = this.preferences.music && !this.preferences.muteAll;
    this.preferences = this.store.update(patch);
    this.audio.setPreferences(this.preferences);
    const musicIsEnabled = this.preferences.music && !this.preferences.muteAll;
    if (this.playing && !this.paused && !musicWasEnabled && musicIsEnabled)
      this.audio.startMusic(this.activeSession.level?.id ?? null);
    this.game?.registry.set("preferences", this.preferences);
    this.game?.events.emit("preferences:changed", this.preferences);
    this.syncSettings();
  }

  private syncSettings(): void {
    required<HTMLInputElement>("#setting-music").checked =
      this.preferences.music;
    required<HTMLInputElement>("#setting-effects").checked =
      this.preferences.effects;
    required<HTMLInputElement>("#setting-haptics").checked =
      this.preferences.haptics;
    required<HTMLInputElement>("#setting-mute").checked =
      this.preferences.muteAll;
    required<HTMLInputElement>("#setting-motion").checked =
      this.preferences.reducedMotion;
    const sensitivity = required<HTMLInputElement>("#setting-sensitivity");
    sensitivity.value = this.preferences.sensitivity.toString();
    required<HTMLOutputElement>("#sensitivity-output").value =
      `${Math.round(this.preferences.sensitivity * 100)}%`;
    const tiltSensitivity = required<HTMLInputElement>(
      "#setting-tilt-sensitivity",
    );
    tiltSensitivity.value = this.preferences.tiltSensitivity.toString();
    required<HTMLOutputElement>("#tilt-sensitivity-output").value =
      `${Math.round(this.preferences.tiltSensitivity * 100)}%`;
    required<HTMLElement>("#tilt-status").textContent = this.preferences
      .tiltEnabled
      ? COPY.ui.settings.tiltEnabled
      : COPY.ui.common.touchAvailable;
    required<HTMLButtonElement>("#tilt-action").textContent = this.preferences
      .tiltEnabled
      ? COPY.ui.settings.disableTilt
      : COPY.ui.settings.enableTilt;
    required<HTMLElement>("#tutorial-status").textContent = this.preferences
      .tutorialComplete
      ? COPY.ui.settings.tutorialCompleted
      : COPY.ui.settings.tutorialNext;
  }

  private syncTutorialControl(): void {
    required<HTMLButtonElement>("#tutorial-skip").hidden =
      !this.playing || this.paused || !this.tutorialVisible;
  }

  private async toggleTilt(): Promise<void> {
    const status = required<HTMLElement>("#tilt-status");
    if (this.preferences.tiltEnabled) {
      this.tilt.disable();
      this.updatePreferences({ tiltEnabled: false });
      status.textContent = COPY.ui.settings.tiltDisabled;
      return;
    }
    const result = await this.tilt.enable(this.preferences.tiltCenter);
    if (result === "enabled") {
      const center = this.tilt.calibrate();
      this.updatePreferences({ tiltEnabled: true, tiltCenter: center });
      this.tilt.disable();
      status.textContent = COPY.ui.settings.tiltCalibrated;
    } else {
      this.updatePreferences({ tiltEnabled: false });
      status.textContent =
        result === "unsupported"
          ? COPY.ui.settings.tiltUnavailable
          : COPY.ui.settings.tiltDenied;
    }
  }

  private announce(message: string): void {
    required<HTMLElement>("#live-region").textContent = "";
    globalThis.setTimeout(
      () => (required<HTMLElement>("#live-region").textContent = message),
      20,
    );
  }

  private registerPwa(): void {
    let update: ((reloadPage?: boolean) => Promise<void>) | null = null;
    update = registerSW({
      onNeedRefresh: () => {
        this.pendingPwaUpdate = true;
        this.applyPwaUpdate = async () => {
          await update?.(true);
        };
        this.applyPendingUpdateAtLaunchBay();
      },
      onOfflineReady: () => {
        const toast = required<HTMLElement>("#toast");
        toast.textContent = COPY.ui.pwa.offlineReady;
        toast.hidden = false;
        globalThis.setTimeout(() => (toast.hidden = true), 3_000);
      },
    });
  }

  private applyPendingUpdateAtLaunchBay(): void {
    if (
      !this.pendingPwaUpdate ||
      !this.applyPwaUpdate ||
      this.playing ||
      required<HTMLElement>("#menu-screen").hidden
    )
      return;
    this.pendingPwaUpdate = false;
    void this.applyPwaUpdate();
  }

  private exposeLocalTestApi(): void {
    if (!["localhost", "127.0.0.1"].includes(globalThis.location.hostname))
      return;
    window.__BUTTROIDS_TEST__ = {
      start: () => this.startQuickPlay(),
      startCampaign: (levelId, comfyMode = false, continued = false) => {
        const level = getCampaignLevel(levelId);
        if (!level) return;
        // Local automation can enter any level to validate hazards and pacing;
        // the player-facing map still enforces unlock order.
        this.progress = {
          ...this.progress,
          highestUnlockedLevel: Math.max(
            this.progress.highestUnlockedLevel,
            levelId,
          ),
        };
        if (comfyMode)
          this.progress = {
            ...this.progress,
            failureStreaks: {
              ...this.progress.failureStreaks,
              [levelId.toString()]: 2,
            },
          };
        this.startCampaign(level, comfyMode, continued);
      },
      forceVictory: () => this.game.events.emit("test:victory"),
      forceDefeat: () => this.game.events.emit("test:defeat"),
      grantTriple: () => this.game.events.emit("test:grant-triple"),
      grantShield: () => this.game.events.emit("test:grant-shield"),
      forceCollision: () => this.game.events.emit("test:force-collision"),
      forceClogs: (target) => this.game.events.emit("test:force-clogs", target),
      forceBossWarning: () => this.game.events.emit("test:boss-warning"),
      forceBoss: () => this.game.events.emit("test:force-boss"),
      forceBossClog: () => this.game.events.emit("test:force-boss-clog"),
      snapshot: () =>
        (this.game.scene.getScene("game") as GameScene).snapshot(),
      progress: () => structuredClone(this.progress),
    };
  }
}
